interface IProps {
  className?: string;
}

// Official EU flag geometry: 3:2 field, 12 stars evenly spaced on a circle of
// radius 1/3 of the height, each star 1/9 of the height across, one point up.
const WIDTH = 27;
const HEIGHT = 18;
const CENTRE = { x: WIDTH / 2, y: HEIGHT / 2 };
const STAR_CIRCLE_RADIUS = HEIGHT / 3;
const STAR_RADIUS = HEIGHT / 18;
// Ratio of a pentagram's inner to outer radius: sin(18°)/sin(54°).
const STAR_INNER_RADIUS = STAR_RADIUS * 0.381966;

// Vendor brand mark — Reflex Blue and Yellow are mandated by the EU, so these
// are deliberately literal rather than design tokens (.claude/rules/design.md).
const REFLEX_BLUE = "#003399";
const EU_YELLOW = "#FFCC00";

const pointOnCircle = (radius: number, degrees: number) => {
  const radians = ((degrees - 90) * Math.PI) / 180;

  return `${(radius * Math.cos(radians)).toFixed(3)},${(radius * Math.sin(radians)).toFixed(3)}`;
};

// One star centred on the origin, alternating outer and inner vertices.
const STAR_POINTS = Array.from({ length: 10 }, (_, vertex) =>
  pointOnCircle(
    vertex % 2 === 0 ? STAR_RADIUS : STAR_INNER_RADIUS,
    vertex * 36,
  ),
).join(" ");

const STAR_CENTRES = Array.from({ length: 12 }, (_, position) => {
  const radians = ((position * 30 - 90) * Math.PI) / 180;

  return {
    x: CENTRE.x + STAR_CIRCLE_RADIUS * Math.cos(radians),
    y: CENTRE.y + STAR_CIRCLE_RADIUS * Math.sin(radians),
  };
});

// Sized by its container rather than a `size` prop: the field is 3:2, so a
// width matched to a square icon box leaves it looking smaller than the lucide
// icons beside it. Filling the box gets the width back.
export const EuFlag = ({ className }: IProps) => {
  return (
    <svg
      className={className}
      width="100%"
      height="auto"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="European Union"
    >
      <rect width={WIDTH} height={HEIGHT} fill={REFLEX_BLUE} />
      {STAR_CENTRES.map((centre) => (
        <polygon
          key={`${centre.x}-${centre.y}`}
          points={STAR_POINTS}
          fill={EU_YELLOW}
          transform={`translate(${centre.x.toFixed(3)} ${centre.y.toFixed(3)})`}
        />
      ))}
    </svg>
  );
};
